import { FunctionComponent, MutableRefObject, useEffect, useRef, useState } from 'react'
import styles from './exportSectionSlotsDialog.module.scss'
import { ExportDataType } from '@/types/Export.ts'
import { Daycare } from '@/types/Daycare.ts'
import { useTranslation } from 'react-i18next'
import moment, { Moment } from 'moment/moment'
import { ApolloClient, useApolloClient, useLazyQuery } from '@apollo/client'
import { GET_EXPORT_DATA } from '@/modules/API/export/getExportData.ts'
import { GET_RATE_SLOTS_AND_SLOTS } from '@/modules/API/staff/staffRate.ts'
import { downloadBase64File, downloadBlob } from '@/modules/utils/downloader.ts'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import Loading from '@/components/Loading/Loading.tsx'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { Section } from '@/types/Section.ts'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { getDaycareClosing, getDaycareOpening } from '@/modules/helper/daycareHelper.ts'
import { RateSlotsAndSlots } from '@/types/StaffRate.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import * as htmlToImage from 'html-to-image'
import { PDFDocument, PDFImage, PDFPage } from 'pdf-lib'
import DiagramExportPage, { SectionDiagramData } from './DiagramExportPage.tsx'

interface ExportSectionSlotsDialogProps {
    open: boolean
    handleClose: () => void
    minDate: string
    daycare: Daycare
    startDate: string
    endDate: string
    preSelectedSections: string[]
    isRpeOrLaep: boolean
}

interface DiagramPage {
    key: string
    date: string
    section: SectionDiagramData
}

const ExportSectionSlotsDialog: FunctionComponent<ExportSectionSlotsDialogProps> = ({
    open,
    handleClose,
    minDate,
    daycare,
    startDate,
    endDate,
    preSelectedSections,
    isRpeOrLaep
}: ExportSectionSlotsDialogProps) => {
    const { t } = useTranslation()

    const client: ApolloClient<object> = useApolloClient()

    const { showNotificationError } = useNotification()

    const defaultDate: Moment = moment().subtract(1, 'month')

    const [fromDate, setFromDate] = useState<string>(
        startDate ? startDate : defaultDate.startOf('month').format('DD-MM-YYYY')
    )
    const [toDate, setToDate] = useState<string>(endDate ? endDate : defaultDate.endOf('month').format('DD-MM-YYYY'))

    const [selectPeriod, setSelectPeriod] = useState<boolean>(startDate !== endDate)

    const [selectedSections, setSelectedSections] = useState<string[]>(preSelectedSections)

    const exportFormats: SelectOptionsDeprecated = [
        {
            options: [
                {
                    value: 'excel',
                    name: t('Spreadsheet')
                },
                {
                    value: 'diagram',
                    name: t('Diagram')
                }
            ]
        }
    ]

    const [selectedExportFormat, setSelectedExportFormat] = useState<string>(exportFormats[0].options[0].value)

    const contentTypeOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    value: 'staffs',
                    name: t('staffs')
                },
                {
                    value: 'childs',
                    name: t('childsPresent')
                },
                {
                    value: 'availableSlots',
                    name: t('availableSlots')
                }
            ]
        }
    ]

    const [selectedContentTypes, setSelectedContentTypes] = useState<string[]>(['staffs', 'childs', 'availableSlots'])

    const [loadingDiagram, setLoadingDiagram] = useState<boolean>(false)
    const [diagramPages, setDiagramPages] = useState<DiagramPage[] | null>(null)
    const diagramPageRefs: MutableRefObject<Map<string, HTMLDivElement>> = useRef<Map<string, HTMLDivElement>>(
        new Map()
    )

    const [getExportData, { data: dataExport, loading: loadingExport }] = useLazyQuery(GET_EXPORT_DATA, {
        variables: {
            daycareIds: [daycare.id],
            from: fromDate,
            to: toDate,
            type: ExportDataType.Calendar,
            sectionIds: selectedSections.map((s: string) => Number(s))
        }
    })

    useEffect(() => {
        if (dataExport && dataExport.getExportData) {
            downloadBase64File(
                dataExport.getExportData.base64Data,
                `${getFileName(t('calendar'), selectPeriod, startDate, toDate).trim()}.xlsx`,
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ).then(() => {
                handleClose()
            })
        }
    }, [dataExport])

    useEffect(() => {
        if (!diagramPages) return

        buildDiagramPdf(diagramPages)
    }, [diagramPages])

    async function buildDiagramPdf(pages: DiagramPage[]): Promise<void> {
        try {
            const pdfDoc: PDFDocument = await PDFDocument.create()

            for (const page of pages) {
                const node: HTMLDivElement | undefined = diagramPageRefs.current.get(page.key)
                if (!node) continue

                const dataUrl: string = await htmlToImage.toPng(node, { pixelRatio: 2 })
                const pngBytes: ArrayBuffer = await fetch(dataUrl).then((res: Response) => res.arrayBuffer())
                const image: PDFImage = await pdfDoc.embedPng(pngBytes)

                const width: number = image.width / 2
                const height: number = image.height / 2
                const pdfPage: PDFPage = pdfDoc.addPage([width, height])
                pdfPage.drawImage(image, { x: 0, y: 0, width, height })
            }

            const pdfBytes: Uint8Array<ArrayBufferLike> = await pdfDoc.save()
            const blob: Blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })

            downloadBlob(blob, `${getFileName(t('Diagram'), selectPeriod, fromDate, toDate).trim()}.pdf`)

            handleClose()
        } catch {
            showNotificationError(t('errorGeneratingPdf'))
        } finally {
            setDiagramPages(null)
            setLoadingDiagram(false)
        }
    }

    async function handleExportDiagram(): Promise<void> {
        if (selectedSections.length === 0) {
            showNotificationError(t('pleaseSelectSection'))
            return
        }

        setLoadingDiagram(true)

        try {
            const from: string = selectPeriod ? fromDate : toDate

            const dates: string[] = getDatesInRange(from, toDate).filter((date: string): boolean =>
                isDaycareOpenOnDate(daycare, date)
            )

            const pagePlans: { date: string; sectionId: string }[] = dates.flatMap((date: string) =>
                selectedSections.map((sectionId: string) => ({ date, sectionId }))
            )

            const fetchedPages: (DiagramPage | null)[] = await Promise.all(
                pagePlans.map(async ({ date, sectionId }): Promise<DiagramPage | null> => {
                    const { data } = await client.query({
                        query: GET_RATE_SLOTS_AND_SLOTS,
                        variables: {
                            daycareId: daycare.id,
                            date,
                            sectionIds: [Number(sectionId)]
                        },
                        fetchPolicy: 'network-only'
                    })

                    const rateSlotsAndSlots: RateSlotsAndSlots = data.getRateSlotsAndSlots as RateSlotsAndSlots

                    if (rateSlotsAndSlots.closingDay) return null

                    return {
                        key: `${date}_${sectionId}`,
                        date,
                        section: {
                            sectionId: Number(sectionId),
                            sectionName: getSectionName(sectionId),
                            data: rateSlotsAndSlots
                        }
                    }
                })
            )

            const pages: DiagramPage[] = fetchedPages.filter((page: DiagramPage | null): page is DiagramPage =>
                Boolean(page)
            )

            if (pages.length === 0) {
                showNotificationError(t('noOpenDayInPeriod'))
                setLoadingDiagram(false)
                return
            }

            setDiagramPages(pages)
        } catch {
            showNotificationError(t('slotsLoadedError'))
            setLoadingDiagram(false)
        }
    }

    function getSectionName(sectionId: string): string {
        return daycare.sections?.find((section: Section): boolean => section.id.toString() === sectionId)?.name || ''
    }

    function handleExport(): void {
        if (selectedExportFormat === 'excel') {
            getExportData()
        } else {
            handleExportDiagram()
        }
    }

    const selectOptionsSections: SelectOptionsDeprecated = [
        {
            options: daycare.sections
                ? daycare.sections.map((section: Section): SelectOptionDeprecated => {
                      return {
                          value: section.id.toString(),
                          name: section.name
                      }
                  })
                : []
        }
    ]

    const isLoading: boolean = loadingExport || loadingDiagram

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('exportCalendar')}></DialogHeader>
            <DialogBody>
                <div className={styles.DialogBody}>
                    {isLoading ? (
                        <Loading overall={false} />
                    ) : (
                        <>
                            <div className={styles.SelectContainer}>
                                <SelectMultiple
                                    label={t(isRpeOrLaep ? 'mySectors' : 'mySections')}
                                    selectOptions={selectOptionsSections}
                                    selectValues={selectedSections}
                                    setSelectValues={setSelectedSections}
                                    className={styles.UserInput}
                                />
                            </div>
                            {selectPeriod ? (
                                <div className={styles.SelectContainer}>
                                    <DatePickerComponent
                                        minDate={minDate}
                                        maxDate={toDate}
                                        value={fromDate}
                                        setValue={setFromDate}
                                        label={t('fromDate')}
                                        className={styles.UserInput}
                                        disablePast={false}
                                    />
                                    <DatePickerComponent
                                        minDate={fromDate}
                                        value={toDate}
                                        setValue={setToDate}
                                        label={t('toDate')}
                                        className={styles.UserInput}
                                        disablePast={false}
                                    />
                                </div>
                            ) : (
                                <div className={styles.SelectContainer}>
                                    <DatePickerComponent
                                        minDate={minDate}
                                        value={toDate}
                                        setValue={setToDate}
                                        label={t('date')}
                                        className={styles.UserInput}
                                        disablePast={false}
                                    />
                                </div>
                            )}
                            <div className={styles.CheckBoxContainer}>
                                <Checkbox
                                    text={t('selectPeriod')}
                                    checked={selectPeriod}
                                    onChange={(checked: boolean) => setSelectPeriod(checked)}
                                    className={styles.CheckBox}
                                />
                            </div>
                            <div className={styles.SelectContainer}>
                                <SelectUnique
                                    label={t('Format')}
                                    selectOptions={exportFormats}
                                    selectValue={selectedExportFormat}
                                    setSelectValue={setSelectedExportFormat}
                                />
                            </div>
                            {selectedExportFormat === 'diagram' && (
                                <div className={styles.SelectContainer}>
                                    <SelectMultiple
                                        label={t('contentToExport')}
                                        selectOptions={contentTypeOptions}
                                        selectValues={selectedContentTypes}
                                        setSelectValues={setSelectedContentTypes}
                                        className={styles.UserInput}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        <ButtonTextIcon
                            type={'export'}
                            onClick={handleExport}
                            className={styles.Button}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            </DialogBody>
            {diagramPages && (
                <div className={styles.HiddenContainer}>
                    {diagramPages.map((page: DiagramPage) => (
                        <div
                            key={page.key}
                            ref={(el: HTMLDivElement | null) => {
                                if (el) diagramPageRefs.current.set(page.key, el)
                            }}
                        >
                            <DiagramExportPage
                                date={page.date}
                                daycare={daycare}
                                section={page.section}
                                includeStaffs={selectedContentTypes.includes('staffs')}
                                includeChilds={selectedContentTypes.includes('childs')}
                                includeAvailableSlots={selectedContentTypes.includes('availableSlots')}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Dialog>
    )
}

export default ExportSectionSlotsDialog

function getFileName(typeString: string, selectPeriod: boolean, startDate: string, toDate: string): string {
    const date: string = selectPeriod
        ? moment(startDate, 'DD-MM-YYYY').format('YYYY-MM-DD') + '_' + moment(toDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
        : moment(startDate, 'DD-MM-YYYY').format('YYYY-MM-DD')

    return `${typeString.toLowerCase()}_${date}`
}

function getDatesInRange(from: string, to: string): string[] {
    const dates: string[] = []
    const current: Moment = moment(from, 'DD-MM-YYYY')
    const end: Moment = moment(to, 'DD-MM-YYYY')

    while (current.isSameOrBefore(end)) {
        dates.push(current.format('DD-MM-YYYY'))
        current.add(1, 'day')
    }

    return dates
}

function isDaycareOpenOnDate(daycare: Daycare, date: string): boolean {
    const day: number = (moment(date, 'DD-MM-YYYY').day() + 6) % 7
    return getDaycareOpening(daycare, day) !== null && getDaycareClosing(daycare, day) !== null
}
